import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  ReguaCobrancaDetalhe,
  ReguaCobrancaEtapas,
  ReguaCobrancaFinanceiro,
  ReguaCobrancaListResponse,
  ReguaCobrancaLog,
  ReguaState,
  ReguaTemplate,
  ReguaTemplateListResponse,
  SalvarReguaResult,
} from '../types';
import {
  aplicarCobrancasEmLote,
  buscarCobrancaEtapas,
  buscarCobrancaFinanceiro,
  buscarCobrancaLogs,
  buscarReguaCobranca,
  buscarReguaTemplate,
  criarCobrancaIndividual,
  encerrarCobranca,
  excluirReguaTemplate,
  exportarCobrancas,
  listarReguaCobrancas,
  listarReguaTemplates,
  pausarCobranca,
  registrarAcordoCobranca,
  registrarPagamentoCobranca,
  retomarCobranca,
  salvarReguaTemplate,
} from './actions';

const initialState: ReguaState = {
  templates: [],
  currentTemplate: null,
  isLoading: false,
  isLoadingDetail: false,
  isSaving: false,
  error: null,
  total: 0,
  cobrancas: [],
  totalCobrancas: 0,
  isLoadingCobrancas: false,
  errorCobrancas: null,
  isCriando: false,
  criarError: null,
  currentCobranca: null,
  isLoadingCobrancaDetail: false,
  errorCobrancaDetail: null,
  cobrancaEtapas: null,
  isLoadingCobrancaEtapas: false,
  errorCobrancaEtapas: null,
  cobrancaLogs: [],
  isLoadingCobrancaLogs: false,
  errorCobrancaLogs: null,
  cobrancaFinanceiro: null,
  isLoadingCobrancaFinanceiro: false,
  errorCobrancaFinanceiro: null,
  isMutandoCobranca: false,
  errorMutacaoCobranca: null,
};

const reguaCobrancaSlice = createSlice({
  name: 'reguaCobranca',
  initialState,
  reducers: {
    clearReguaCurrentTemplate: (state) => {
      state.currentTemplate = null;
      state.error = null;
    },
    clearReguaCurrentCobranca: (state) => {
      state.currentCobranca = null;
      state.errorCobrancaDetail = null;
      state.cobrancaEtapas = null;
      state.errorCobrancaEtapas = null;
      state.cobrancaLogs = [];
      state.errorCobrancaLogs = null;
      state.cobrancaFinanceiro = null;
      state.errorCobrancaFinanceiro = null;
      state.errorMutacaoCobranca = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listarReguaTemplates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        listarReguaTemplates.fulfilled,
        (state, action: PayloadAction<ReguaTemplateListResponse>) => {
          state.isLoading = false;
          state.templates = action.payload.items;
          state.total = action.payload.total;
        },
      )
      .addCase(listarReguaTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? null;
      })

      .addCase(buscarReguaTemplate.pending, (state) => {
        state.isLoadingDetail = true;
        state.error = null;
      })
      .addCase(buscarReguaTemplate.fulfilled, (state, action: PayloadAction<ReguaTemplate>) => {
        state.isLoadingDetail = false;
        state.currentTemplate = action.payload;
      })
      .addCase(buscarReguaTemplate.rejected, (state, action) => {
        state.isLoadingDetail = false;
        state.error = action.payload ?? null;
      })

      .addCase(listarReguaCobrancas.pending, (state) => {
        state.isLoadingCobrancas = true;
        state.errorCobrancas = null;
      })
      .addCase(
        listarReguaCobrancas.fulfilled,
        (state, action: PayloadAction<ReguaCobrancaListResponse>) => {
          state.isLoadingCobrancas = false;
          state.cobrancas = action.payload.items;
          state.totalCobrancas = action.payload.total;
        },
      )
      .addCase(listarReguaCobrancas.rejected, (state, action) => {
        state.isLoadingCobrancas = false;
        state.errorCobrancas = action.payload as string;
      })

      .addCase(criarCobrancaIndividual.pending, (state) => {
        state.isCriando = true;
        state.criarError = null;
      })
      .addCase(criarCobrancaIndividual.fulfilled, (state) => {
        state.isCriando = false;
      })
      .addCase(criarCobrancaIndividual.rejected, (state, action) => {
        state.isCriando = false;
        state.criarError = action.payload?.mensagem ?? 'Erro ao criar cobrança';
      })

      .addCase(buscarReguaCobranca.pending, (state) => {
        state.isLoadingCobrancaDetail = true;
        state.errorCobrancaDetail = null;
      })
      .addCase(
        buscarReguaCobranca.fulfilled,
        (state, action: PayloadAction<ReguaCobrancaDetalhe>) => {
          state.isLoadingCobrancaDetail = false;
          state.currentCobranca = action.payload;
        },
      )
      .addCase(buscarReguaCobranca.rejected, (state, action) => {
        state.isLoadingCobrancaDetail = false;
        state.errorCobrancaDetail = action.payload as string;
      })

      .addCase(buscarCobrancaEtapas.pending, (state) => {
        state.isLoadingCobrancaEtapas = true;
        state.errorCobrancaEtapas = null;
      })
      .addCase(
        buscarCobrancaEtapas.fulfilled,
        (state, action: PayloadAction<ReguaCobrancaEtapas>) => {
          state.isLoadingCobrancaEtapas = false;
          state.cobrancaEtapas = action.payload;
        },
      )
      .addCase(buscarCobrancaEtapas.rejected, (state, action) => {
        state.isLoadingCobrancaEtapas = false;
        state.errorCobrancaEtapas = action.payload ?? 'Erro ao carregar as etapas';
      })

      .addCase(buscarCobrancaLogs.pending, (state) => {
        state.isLoadingCobrancaLogs = true;
        state.errorCobrancaLogs = null;
      })
      .addCase(buscarCobrancaLogs.fulfilled, (state, action: PayloadAction<ReguaCobrancaLog[]>) => {
        state.isLoadingCobrancaLogs = false;
        state.cobrancaLogs = action.payload;
      })
      .addCase(buscarCobrancaLogs.rejected, (state, action) => {
        state.isLoadingCobrancaLogs = false;
        state.errorCobrancaLogs = action.payload ?? 'Erro ao carregar os logs';
      })

      .addCase(buscarCobrancaFinanceiro.pending, (state) => {
        state.isLoadingCobrancaFinanceiro = true;
        state.errorCobrancaFinanceiro = null;
      })
      .addCase(
        buscarCobrancaFinanceiro.fulfilled,
        (state, action: PayloadAction<ReguaCobrancaFinanceiro>) => {
          state.isLoadingCobrancaFinanceiro = false;
          state.cobrancaFinanceiro = action.payload;
        },
      )
      .addCase(buscarCobrancaFinanceiro.rejected, (state, action) => {
        state.isLoadingCobrancaFinanceiro = false;
        state.errorCobrancaFinanceiro = action.payload ?? 'Erro ao carregar o financeiro';
      })

      .addCase(salvarReguaTemplate.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(salvarReguaTemplate.fulfilled, (state, action: PayloadAction<SalvarReguaResult>) => {
        state.isSaving = false;
        state.currentTemplate = action.payload.template;
      })
      .addCase(salvarReguaTemplate.rejected, (state) => {
        state.isSaving = false;
      })

      .addCase(excluirReguaTemplate.fulfilled, (state, action: PayloadAction<string>) => {
        state.templates = state.templates.filter((template) => template.id !== action.payload);
        state.total = Math.max(0, state.total - 1);
      })

      .addMatcher(
        isAnyOf(
          pausarCobranca.pending,
          retomarCobranca.pending,
          registrarPagamentoCobranca.pending,
          encerrarCobranca.pending,
          aplicarCobrancasEmLote.pending,
          registrarAcordoCobranca.pending,
          exportarCobrancas.pending,
        ),
        (state) => {
          state.isMutandoCobranca = true;
          state.errorMutacaoCobranca = null;
        },
      )
      .addMatcher(
        isAnyOf(
          pausarCobranca.fulfilled,
          retomarCobranca.fulfilled,
          registrarPagamentoCobranca.fulfilled,
          encerrarCobranca.fulfilled,
          aplicarCobrancasEmLote.fulfilled,
          registrarAcordoCobranca.fulfilled,
          exportarCobrancas.fulfilled,
        ),
        (state) => {
          state.isMutandoCobranca = false;
        },
      )
      .addMatcher(
        isAnyOf(
          pausarCobranca.rejected,
          retomarCobranca.rejected,
          registrarPagamentoCobranca.rejected,
          encerrarCobranca.rejected,
          aplicarCobrancasEmLote.rejected,
          registrarAcordoCobranca.rejected,
          exportarCobrancas.rejected,
        ),
        (state, action) => {
          state.isMutandoCobranca = false;
          state.errorMutacaoCobranca = action.payload ?? 'Não foi possível concluir a ação.';
        },
      );
  },
});

export const { clearReguaCurrentTemplate, clearReguaCurrentCobranca } = reguaCobrancaSlice.actions;
export const reguaCobrancaReducer = reguaCobrancaSlice.reducer;
